import { api } from '@packages/backend/convex/_generated/api';
import {
  byNewestFirst,
  getLinkedMenuItemCount,
  getLinkedMenuItemNames,
  type ModifierGroup,
  modifierGroupSchema,
  sortByDisplayIndex,
} from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { Settings } from 'lucide-react';
import { useState } from 'react';
import { ActionButton } from '@/components/ActionButton';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MenuRearrangeList } from '@/routes/restaurant/menu/MenuRearrangeList';
import { useIsMenuEditable } from '@/routes/restaurant/menu/useIsMenuEditable';
import { useMenu } from '@/routes/restaurant/useRestaurantData';
import { ModifierGroupDialog } from './ModifierGroupDialog';
import { ModifiersTable } from './ModifiersTable';

export const Route = createFileRoute('/r/$restaurantId/menu/modifiers')({
  component: ModifierListPage,
});

function ModifierListPage() {
  const convex = useConvex();
  const menu = useMenu();
  const isMenuEditable = useIsMenuEditable();

  const [newModifierGroup, setNewModifierGroup] = useState<ModifierGroup | null>(null);
  // keep track of id rather than boolean value, as there is many mod groups on the one page
  const [settingsGroupUuid, setSettingsGroupUuid] = useState<string | null>(null);
  const [rearrangingGroupId, setRearrangingGroupId] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<'direct' | 'shared'>('shared');

  if (menu === null) {
    return null; // Handled by menu items tab
  }

  const sortedModifierGroups = [...menu.modifierGroups]
    .sort(byNewestFirst)
    .filter((modifierGroup) => {
      if (filterType === 'shared') return modifierGroup.isShared;
      if (filterType === 'direct') return !modifierGroup.isShared;
      return true;
    });

  if (menu.categories.length === 0) {
    return (
      <div className={'bg-white p-4 text-center border'}>
        <div>Add some categories first.</div>
      </div>
    );
  }

  return (
    <div className={'xl:w-2/3 grid gap-8'}>
      <div className="flex items-center gap-1">
        <Tabs
          onValueChange={(value) => setFilterType(value === 'direct' ? 'direct' : 'shared')}
          value={filterType}
        >
          <TabsList>
            <TabsTrigger value="shared">Shared</TabsTrigger>
            <TabsTrigger value="direct">Item specific</TabsTrigger>
          </TabsList>
        </Tabs>
        {isMenuEditable && (
          <ActionButton
            disabled={filterType !== 'shared'}
            disabledTooltipPosition={'bottom'}
            disabledTooltipText={'Create item specific modifiers via "Menu items" tab'}
            onClick={() => setNewModifierGroup(modifierGroupSchema.decode({ name: '' }))}
            size="sm"
            variant="link"
          >
            Create new modifier group
          </ActionButton>
        )}
      </div>

      {sortedModifierGroups.map((modifierGroup) => {
        const linkedMenuItemNames = getLinkedMenuItemNames({
          menuItems: menu.menuItems,
          modifierGroupUuid: modifierGroup.uuid,
        });

        return (
          <div key={modifierGroup.uuid}>
            {rearrangingGroupId !== modifierGroup.uuid && (
              <div className="flex justify-between items-center">
                <div className={'flex gap-2 items-center'}>
                  <h3 className="text-xl leading-7 font-medium sm:truncate sm:text-2xl sm:tracking-tight mb-2 ">
                    {modifierGroup.name}
                  </h3>
                  <ModifierGroupDialog
                    isOpen={settingsGroupUuid === modifierGroup.uuid}
                    linkedMenuItemsCount={getLinkedMenuItemCount({
                      menuItems: menu.menuItems,
                      modifierGroupUuid: modifierGroup.uuid,
                    })}
                    menuId={menu._id}
                    modifierGroup={modifierGroup}
                    modifiers={menu.modifiers.filter(
                      (m) => m.modifierGroupUuid === modifierGroup.uuid,
                    )}
                    onOpenChange={(open) => setSettingsGroupUuid(open ? modifierGroup.uuid : null)}
                    triggerButton={
                      <ActionButton className={'h-8 mt-0.5 mb-1'} size={'sm'} variant="outline">
                        <Settings size={16} />
                      </ActionButton>
                    }
                  />
                  <div className={'text-xs text-muted-foreground'}>
                    {linkedMenuItemNames.length === 0 && '(not linked)'}
                    {linkedMenuItemNames.length > 0 &&
                      linkedMenuItemNames.length <= 3 &&
                      `Linked to ${linkedMenuItemNames.join(', ')}`}
                    {linkedMenuItemNames.length > 3 && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <ActionButton
                            className="h-auto p-0 text-xs text-muted-foreground"
                            variant="link"
                          >
                            Linked to {linkedMenuItemNames.length} items...
                          </ActionButton>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-4 py-2">
                          <ul>
                            {linkedMenuItemNames.map((item, index) => (
                              <li className="text-sm" key={item}>
                                {item}
                              </li>
                            ))}
                          </ul>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {modifierGroup.qtyMax > 1 && <Badge variant={'outline'}>Multi-choice</Badge>}
                  {modifierGroup.modifierType === 'SIZE' && (
                    <Badge variant={'outline'}>Size Group</Badge>
                  )}
                </div>
              </div>
            )}

            {rearrangingGroupId === modifierGroup.uuid ? (
              <>
                <div className="flex justify-between">
                  <h3 className="text-xl leading-7 font-medium sm:truncate sm:text-2xl sm:tracking-tight mb-2 ">
                    {modifierGroup.name}
                  </h3>
                </div>
                <MenuRearrangeList
                  items={sortByDisplayIndex(
                    menu.modifiers.filter((m) => m.modifierGroupUuid === modifierGroup.uuid),
                  )}
                  itemsLabel="modifiers"
                  onRearrangingDone={() => setRearrangingGroupId(null)}
                  onSave={(sortedModifiersUuids) =>
                    convex.mutation(api.menus.updateModifiersSortOrder, {
                      menuId: menu._id,
                      sortedModifiersUuids,
                    })
                  }
                />
              </>
            ) : (
              <ModifiersTable
                menuId={menu._id}
                modifierGroup={modifierGroup}
                modifiers={sortByDisplayIndex(
                  menu.modifiers.filter((m) => m.modifierGroupUuid === modifierGroup.uuid),
                )}
                onRearrange={() => setRearrangingGroupId(modifierGroup.uuid)}
              />
            )}
          </div>
        );
      })}

      {newModifierGroup && (
        <ModifierGroupDialog
          isOpen={true}
          linkedMenuItemsCount={0}
          menuId={menu._id}
          modifierGroup={newModifierGroup}
          modifiers={[]}
          onOpenChange={() => setNewModifierGroup(null)}
        />
      )}
    </div>
  );
}
